import styled from 'styled-components';
import Head from 'next/head';
import type { DirectoryPageViewModel } from '@lib/page-data/directory.map';
import type { ShopsPageViewModel } from '@lib/page-data/shops.map';
import type { Locale } from '@lib/locale';
import type { PageInjectedProps } from '../Pages/Page';
import ShopGroup from './ShopGroup';
import ShopNavigationLetters from './ShopNavigationLetter';

const DividerDiv = styled.div`
  width: 100%;
  border-bottom: ${(p) => `1px solid ${p.theme.c.border.primary}`};
`;

// This component only uses these values to build its document metadata. Keep
// its contract independent from the full navigation GraphQL fragment so page
// queries can select just the fields they need.
type GlobalPageConfig = {
  defaultMetaDescription?: string | null;
  pageTitleBase?: string | null;
};

type Props = {
  shopOrder: DirectoryPageViewModel['shopOrder'] | ShopsPageViewModel['shopOrder'];
  locale: Locale;
  isMobile?: boolean;
  globalPageConfig?: GlobalPageConfig;
  globalShopConfig?: DirectoryPageViewModel['globalShopConfig'];
} & Partial<Pick<PageInjectedProps, 'userData' | 'pageStateModifiers'>>;

const ShopsDirectoryContent = (props: Props) => {
  const {
    userData,
    pageStateModifiers,
    shopOrder = {},
    locale,
    isMobile,
    globalPageConfig,
    globalShopConfig,
  } = props;

  const order = shopOrder.order || [];

  const CreateShopGroup = (bool?: boolean) => order.map((letter, index, self) => (
    <ShopGroup
      key={`shop-group-${letter}`}
      letter={letter}
      shops={shopOrder[letter]}
      locale={locale}
      index={index}
      length={self.length}
      pageStateModifiers={pageStateModifiers}
      userData={userData}
      isMobile={bool}
    />
  ));

  const {
    metaDescription = '',
    directoryPageTitle = '',
  } = globalShopConfig || {};

  const metaDescriptionToUse = (
    metaDescription || globalPageConfig?.defaultMetaDescription
  ) ?? undefined;
  const pageTitleBase = globalPageConfig?.pageTitleBase
    ? globalPageConfig.pageTitleBase : '';
  const pageTitleToUse = `${directoryPageTitle} ${pageTitleBase}`;

  return (
    <div>
      <Head>
        <title>{pageTitleToUse}</title>
        <meta name="description" content={metaDescriptionToUse} />
      </Head>

      <ShopNavigationLetters shopOrder={shopOrder} isMobile={isMobile} />
      <DividerDiv />
      {CreateShopGroup(isMobile)}
    </div>
  );
};

export default ShopsDirectoryContent;
